import { Button } from "src/components/atoms/Button";
import styles from "./styles.module.css";
import plusSvg from "../../../../assets/icons/plus.svg";
import { UserAvatar } from "src/components/molecules/UserAvatar";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { openModal, closeModal } from "src/store/slices/modalSlice";
import { setSelectedClient } from "src/store/slices/selectedClientSlice";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "src/api/client";
import { ClientItem } from "src/api/types";

export const SidebarClient: React.FC = () => {
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const usersData = clients.filter((client) => client.status === "approved");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const modalData = useAppSelector(
    (state) => state.modal.data.sidebarClient as { mode?: string } | undefined,
  );
  const isChangeClientMode = modalData?.mode === "changeClient";

  useEffect(() => {
    apiClient.getClients({
      status: "approved",
      page_size: 100,
      orders: ["client__first_name"],
    })
      .then((res) => setClients(res.items))
      .catch(() => setError("Failed to load clients"));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        !target.closest("[data-sidebar-client]") &&
        !target.closest("[data-sidebar-client-trigger]")
      ) {
        dispatch(closeModal({ type: "sidebarClient" }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dispatch]);

  const handleMouseLeave = () => {
    setTimeout(() => {
      if (!document.querySelector("[data-sidebar-client-trigger]:hover")) {
        dispatch(closeModal({ type: "sidebarClient" }));
      }
    }, 50);
  };

  const openAddNewClient = () => {
    dispatch(closeModal({ type: "sidebarClient" }));
    dispatch(openModal({ type: "addNewClient" }));
  };

  const handleClientSelect = (user: ClientItem) => {
    dispatch(closeModal({ type: "sidebarClient" }));
    if (isChangeClientMode) {
      dispatch(setSelectedClient({
        id: user.id,
        name: user.name,
        email: user.email,
        state: user.state,
        avatarUrl: user.avatar || "",
        status: "approved",
        link_id: user.link_id,
      }));
      if (/^\/clients\/\d+\/?$/.test(location.pathname)) {
        navigate(`/clients/${user.id}`, {
          replace: true,
          state: {
            linkId: user.link_id,
            status: user.status ?? "approved",
          },
        });
      }
    } else {
      navigate(`/clients/${user.id}`, {
        state: {
          linkId: user.link_id,
          status: user.status ?? "approved",
        },
      });
    }
  };

  return (
    <div
      data-sidebar-client="true"
      className={styles.modalContent}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.modalTitle}>Client</div>
      <Button
        text="Add new client"
        icon={plusSvg}
        onClick={openAddNewClient}
      />
      <ErrorBlock error={error} />
      <div className={styles.modalList}>
        {usersData.map((user) => (
          <div
            key={user.id}
            className={styles.clientItem}
            onClick={() => handleClientSelect(user)}
          >
            <UserAvatar
              user={{
                ...user,
                avatarUrl: user.avatar || "",
              }}
              size="compact"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
